import React, { useCallback, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { useNavigate, useParams } from 'react-router-dom';

import { useSession } from '../../../../hooks/useSession';
import { isSystemOwner } from '../../../../modules/session';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux/useApp';
import { CONTENT_MODERATION_ROUTES, KEYBOARD_SHORTCUTS } from '../../constants';
import {
  selectBodyHashItems,
  selectClusterData,
  selectDecisionSubmitLoading,
  selectDetailData,
  selectFanOutRunning,
  selectFanOutSelected,
  selectKeyboardEnabled,
  selectLockHolder,
  selectLockState,
  selectModal,
} from '../../store/contentModerationSelectors';
import {
  fanOutReset,
  fanOutSelectToggle,
  modalClose,
  modalOpen,
} from '../../store/contentModerationActions';
import {
  acquireLockThunk,
  blockEventsFanOutThunk,
  closeEventForReviewThunk,
  resetDecisionThunk,
  submitDecisionWithReasonThunk,
} from '../../store/contentModerationThunks';
import {
  selectDecisionResetLoading,
  selectFanOutResults,
} from '../../store/contentModerationSelectors';
import { isDisagreementReasonRequired } from '../../helpers/isDisagreementReasonRequired';
import { useEventDetail } from '../../hooks/useEventDetail';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useReviewLockHeartbeat } from '../../hooks/useReviewLockHeartbeat';
import LoadingState from '../../components/EmptyState/LoadingState';
import ErrorState from '../../components/EmptyState/ErrorState';
import BlockConfirmationModal, {
  BlockSubmitPayload,
} from '../modals/BlockConfirmationModal';
import ResetDecisionModal from '../modals/ResetDecisionModal';
import AccountClusterPill from './AccountClusterPill';
import AiSignalStrip from './AiSignalStrip';
import ApproveReasonPrompt from './ApproveReasonPrompt';
import BodyHashClusterSection from './BodyHashClusterSection';
import ImageBlobSnapshotCard from './ImageBlobSnapshotCard';
import LockBanner from './LockBanner';
import PageSnapshotCard from './PageSnapshotCard';
import ResetBanner from './ResetBanner';
import ReviewHeader from './ReviewHeader';
import StickyActionBar from './StickyActionBar';
import styles from './review.module.css';

export function ReviewEventScreen(): React.ReactElement {
  const params = useParams<{ id: string }>();
  // params.id is just whatever string the user typed into the URL — coerce to
  // a finite integer and treat anything else (NaN, "abc", "12.5") as null so
  // we never fire lock/detail fetches against /event/NaN.
  const parsedId = params.id != null ? Number(params.id) : NaN;
  const eventId =
    Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const session = useSession();
  const owner = isSystemOwner(session);

  const detailResult = useEventDetail(eventId);
  const lockState = useAppSelector(selectLockState);
  const lockHolder = useAppSelector(selectLockHolder);
  const cluster = useAppSelector(selectClusterData);
  const bodyHashItems = useAppSelector(selectBodyHashItems);
  const fanOutSelected = useAppSelector(selectFanOutSelected);
  const fanOutRunning = useAppSelector(selectFanOutRunning);
  const fanOutResults = useAppSelector(selectFanOutResults);
  const decisionSubmitting = useAppSelector(selectDecisionSubmitLoading);
  const decisionResetting = useAppSelector(selectDecisionResetLoading);
  const modal = useAppSelector(selectModal);
  const keyboardEnabled = useAppSelector(selectKeyboardEnabled);

  const [approvePromptOpen, setApprovePromptOpen] = useState(false);

  useReviewLockHeartbeat(eventId, lockState === 'Held');

  const handleApprove = useCallback(async () => {
    if (!detailResult.detail || eventId == null) return;
    const score = detailResult.detail.event.aiScore;
    const reasonRequired = isDisagreementReasonRequired(score, 'approve');
    if (reasonRequired) {
      setApprovePromptOpen(true);
      return;
    }
    await submitDecisionWithReasonThunk({
      eventId,
      aiScore: score,
      body: { decision: 'approve', disagreementReason: null, notes: null },
    })(dispatch);
    navigate(CONTENT_MODERATION_ROUTES.queue);
  }, [detailResult.detail, eventId, dispatch, navigate]);

  const handleApprovePromptSubmit = useCallback(
    async (data: { reason: string; notes: string | null }) => {
      if (!detailResult.detail || eventId == null) return;
      await submitDecisionWithReasonThunk({
        eventId,
        aiScore: detailResult.detail.event.aiScore,
        body: {
          decision: 'approve',
          disagreementReason: data.reason as never,
          notes: data.notes,
        },
      })(dispatch);
      setApprovePromptOpen(false);
      navigate(CONTENT_MODERATION_ROUTES.queue);
    },
    [detailResult.detail, eventId, dispatch, navigate],
  );

  const handleBlock = useCallback(() => {
    dispatch(modalOpen('block'));
  }, [dispatch]);

  const handleSkip = useCallback(async () => {
    if (eventId == null) return;
    await closeEventForReviewThunk(eventId)(dispatch);
    navigate(CONTENT_MODERATION_ROUTES.queue);
  }, [eventId, dispatch, navigate]);

  const handleRetryLock = useCallback(() => {
    if (eventId != null) {
      void acquireLockThunk(eventId)(dispatch);
    }
  }, [eventId, dispatch]);

  const handleReset = useCallback(() => {
    dispatch(modalOpen('reset'));
  }, [dispatch]);

  useKeyboardShortcuts(
    {
      [KEYBOARD_SHORTCUTS.approve]: handleApprove,
      [KEYBOARD_SHORTCUTS.block]: handleBlock,
      [KEYBOARD_SHORTCUTS.skip]: handleSkip,
    },
    {
      enabled:
        keyboardEnabled
        && modal === 'none'
        && !approvePromptOpen
        && lockState === 'Held',
    },
  );

  if (eventId == null) {
    return (
      <ErrorState
        title={I18n.t('contentModeration.detail.error.invalidId')}
        onRetry={() => navigate(CONTENT_MODERATION_ROUTES.queue)}
        retryLabel={I18n.t('contentModeration.detail.error.backToQueue')}
        testId="review-error"
      />
    );
  }

  if (!detailResult.detail) {
    // Default to the loading state on initial mount: the detail thunk hasn't
    // dispatched _REQUESTED yet (Record's isRecordLoading is still false), so
    // gating on detailLoading alone shows ErrorState for one frame. Only show
    // ErrorState once we actually have an error from the detail fetch.
    const hasError = detailResult.error != null;
    return (
      <div className={styles.screen} data-testid="content-moderation-review">
        <LockBanner
          state={lockState}
          holder={lockHolder}
          onRetry={handleRetryLock}
          testId="lock-banner"
        />
        {hasError ? (
          <ErrorState
            title={I18n.t('contentModeration.detail.error.notFound')}
            onRetry={() => navigate(CONTENT_MODERATION_ROUTES.queue)}
            retryLabel={I18n.t('contentModeration.detail.error.backToQueue')}
            testId="review-error"
          />
        ) : lockState === 'HeldByOther' ? null : (
          <LoadingState testId="review-loading" />
        )}
      </div>
    );
  }

  const { event, snapshot, activeDecision } = detailResult.detail;
  const actionsDisabled =
    lockState !== 'Held' || decisionSubmitting || fanOutRunning;

  return (
    <div className={styles.screen} data-testid="content-moderation-review">
      <LockBanner
        state={lockState}
        holder={lockHolder}
        onRetry={handleRetryLock}
        testId="lock-banner"
      />
      <ResetBanner
        decision={activeDecision}
        canReset={owner}
        onReset={handleReset}
        testId="reset-banner"
      />

      <ReviewHeader event={event} snapshot={snapshot} testId="review-header" />

      <AiSignalStrip event={event} snapshot={snapshot} testId="ai-signal-strip" />

      <AccountClusterPill
        accountId={event.linkedAccountId}
        pendingCount={cluster?.pendingCount ?? 0}
        testId="account-cluster-pill"
      />

      {event.subjectKind === 'blob' ? (
        <ImageBlobSnapshotCard event={event} snapshot={snapshot} testId="image-blob-snapshot" />
      ) : (
        <PageSnapshotCard event={event} snapshot={snapshot} testId="page-snapshot" />
      )}

      <BodyHashClusterSection
        items={bodyHashItems}
        selectedIds={fanOutSelected}
        onToggle={(id) => dispatch(fanOutSelectToggle(id))}
        testId="body-hash-cluster"
      />

      <ApproveReasonPrompt
        open={approvePromptOpen}
        onCancel={() => setApprovePromptOpen(false)}
        onSubmit={handleApprovePromptSubmit}
        isSubmitting={decisionSubmitting}
        testId="approve-prompt"
      />

      <StickyActionBar
        onApprove={handleApprove}
        onBlock={handleBlock}
        onSkip={handleSkip}
        disabled={actionsDisabled}
        testId="action-bar"
      />

      <BlockConfirmationModal
        open={modal === 'block'}
        detail={detailResult.detail}
        pendingCount={cluster?.pendingCount ?? 0}
        bodyHashItems={bodyHashItems}
        selectedIds={fanOutSelected.filter((id) => id !== event.contentModerationEventId)}
        fanOutResults={fanOutResults}
        fanOutRunning={fanOutRunning}
        isSubmitting={decisionSubmitting}
        onCancel={() => {
          dispatch(modalClose());
          dispatch(fanOutReset());
        }}
        onToggleDuplicate={(id) => dispatch(fanOutSelectToggle(id))}
        onConfirm={async (payload: BlockSubmitPayload) => {
          await blockEventsFanOutThunk(payload.selectedEventIds, {
            decision: 'block',
            disagreementReason: payload.disagreementReason,
            notes: payload.notes,
          })(dispatch);
          dispatch(modalClose());
          navigate(CONTENT_MODERATION_ROUTES.queue);
        }}
        testId="block-modal"
      />

      <ResetDecisionModal
        open={modal === 'reset'}
        decision={activeDecision}
        isSubmitting={decisionResetting}
        onCancel={() => dispatch(modalClose())}
        onConfirm={async (reason: string) => {
          if (!activeDecision) return;
          const result = await resetDecisionThunk(activeDecision.id, reason)(dispatch);
          if (result.ok) {
            dispatch(modalClose());
          }
        }}
        testId="reset-modal"
      />
    </div>
  );
}

export default ReviewEventScreen;
